import { Box, Button, Divider, Flex, Icon, IconName, Text } from '@televet/kibble-ui';
import { format } from 'date-fns';
import React, { useMemo, useState } from 'react';
import {
  LoyaltyPointDeltaSource,
  LoyaltyRewardRedemptionStatus,
  RedemptionHistoryFragment,
  RewardHistoryFragment,
} from 'shared/types/graphql';

interface RewardHistoryNodeProps {
  node: RewardHistoryFragment;
}

const buildPointGrantingTitle = (points: number, source: LoyaltyPointDeltaSource): string => {
  if (source === LoyaltyPointDeltaSource.Merge) {
    return `Transferred ${points} Points From Previous Program`;
  }

  return `Earned ${points} Points`;
};

const buildRewardRedemptionTitle = (status: LoyaltyRewardRedemptionStatus, points?: number): string => {
  switch (status) {
    case LoyaltyRewardRedemptionStatus.Fulfilled:
      return 'Fulfilled Reward';
    case LoyaltyRewardRedemptionStatus.Canceled:
    case LoyaltyRewardRedemptionStatus.Canceling:
      return 'Cancelled Reward';
    default:
      return `Claimed Reward Worth ${points} Points`;
  }
};

const issueByMarkup = (firstName: string, lastName: string): JSX.Element => {
  return (
    <Flex mb={2}>
      <Box>
        <Icon name="person" size="md" />
      </Box>
      <Text ml={2} size="sm" as="p">
        Issued By:
        <Text fontWeight="bold" ml={1}>
          {firstName} {lastName}
        </Text>
      </Text>
    </Flex>
  );
};

const getIssuedByMarkupOfActingUserByCurrentRedemptionStatus = (
  historyEntries: RedemptionHistoryFragment[],
  status: LoyaltyRewardRedemptionStatus,
): JSX.Element => {
  let historyEntry = [];
  if (status === LoyaltyRewardRedemptionStatus.PendingFulfillment) {
    historyEntry = historyEntries.filter(
      (historyEntry) => historyEntry.status === LoyaltyRewardRedemptionStatus.Requested,
    );
  } else {
    historyEntry = historyEntries.filter((historyEntry) => historyEntry.status === status);
  }

  const [firstHistoryEntry] = historyEntry;
  if (!!firstHistoryEntry?.actingUser) {
    const result = {
      firstName: firstHistoryEntry.actingUser.firstName,
      lastName: firstHistoryEntry.actingUser.lastName,
    };
    return issueByMarkup(result.firstName, result.lastName);
  } else {
    return <></>;
  }
};

const VIEW_MORE_MAX_CHAR = 40;

export const RewardHistoryNode = ({ node }: RewardHistoryNodeProps): JSX.Element => {
  const { source, points, relatedRedemptionHistoryEntry, userGrant } = node;
  const currentRedemption = relatedRedemptionHistoryEntry?.redemption;
  const redemptionStatus = currentRedemption?.status;
  const [viewMore, setViewMore] = useState(false);

  const nodeColor = useMemo(() => {
    if (source === LoyaltyPointDeltaSource.RewardRedemption && redemptionStatus) {
      switch (redemptionStatus) {
        case LoyaltyRewardRedemptionStatus.PendingFulfillment:
          return 'text.danger';
        case LoyaltyRewardRedemptionStatus.Fulfilled:
          return 'text.warning';
        default:
          return 'text.default';
      }
    }

    // UserGrant, Migration and InvoiceSpend
    return 'text.success';
  }, [redemptionStatus, source]);

  const iconName = useMemo((): IconName => {
    if (source === LoyaltyPointDeltaSource.RewardRedemption && redemptionStatus) {
      switch (redemptionStatus) {
        case LoyaltyRewardRedemptionStatus.PendingFulfillment:
          return 'minus';
        case LoyaltyRewardRedemptionStatus.Fulfilled:
          return 'checkmark';
        default:
          return 'close';
      }
    }

    // UserGrant, Migration and InvoiceSpend
    return 'plus';
  }, [redemptionStatus, source]);

  const clinicNote =
    userGrant?.internalNotes && userGrant.internalNotes.length > VIEW_MORE_MAX_CHAR && !viewMore
      ? `${userGrant.internalNotes.substring(0, VIEW_MORE_MAX_CHAR)}...`
      : userGrant?.internalNotes;

  const currentRedemptionHistoryEntries = relatedRedemptionHistoryEntry?.redemption.redemptionHistories;
  const [requestedRedemptionHistoryEntry] = useMemo(
    () =>
      currentRedemptionHistoryEntries?.filter(
        (redemptionHistoryEntry) => redemptionHistoryEntry.status === LoyaltyRewardRedemptionStatus.Requested,
      ) || [],
    [currentRedemptionHistoryEntries],
  );

  return (
    <Flex mb={2}>
      <Flex flexDirection="column" alignItems="center">
        <Flex
          bgColor={nodeColor}
          color="background.default"
          borderRadius="base"
          alignItems="center"
          justifyContent="center"
          mt="5px"
        >
          <Icon name={iconName} size="xs" />
        </Flex>
        <Divider orientation="vertical" mt={2} />
      </Flex>
      <Box ml={5} w="100%">
        <Flex alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Text as="p" fontWeight="bold" mb={2}>
              {redemptionStatus && source === LoyaltyPointDeltaSource.RewardRedemption
                ? buildRewardRedemptionTitle(
                    redemptionStatus,
                    relatedRedemptionHistoryEntry?.redemption.reward.pointCost,
                  )
                : buildPointGrantingTitle(points, source)}
            </Text>
            {source === LoyaltyPointDeltaSource.UserGrant && userGrant && (
              <Box>
                {userGrant.publicNotes && (
                  <Text as="p" size="sm" mb={1}>
                    {userGrant.publicNotes}
                  </Text>
                )}
                {userGrant.internalNotes && (
                  <Flex flexDirection="column" p={2} bgColor="background.primaryAlpha.hover" mb={1}>
                    <Text as="p" size="sm">
                      <Text fontWeight="bold" size="sm" mr={1}>
                        Clinic Note:
                      </Text>
                      {clinicNote}
                    </Text>
                    {userGrant.internalNotes.length > VIEW_MORE_MAX_CHAR && (
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={(): void => setViewMore(!viewMore)}
                        alignSelf="flex-end"
                      >
                        View {viewMore ? 'Less' : 'More'}
                      </Button>
                    )}
                  </Flex>
                )}
              </Box>
            )}
            {source === LoyaltyPointDeltaSource.UserGrant &&
              userGrant &&
              issueByMarkup(userGrant.grantingUser.firstName, userGrant.grantingUser.lastName)}
            {relatedRedemptionHistoryEntry && (
              <Flex mb={1}>
                <Icon name="award" size="md" variant="warning" />
                <Text ml={2} size="sm">
                  {relatedRedemptionHistoryEntry.redemption.reward.title}
                </Text>
              </Flex>
            )}
            {source === LoyaltyPointDeltaSource.RewardRedemption &&
              (redemptionStatus === LoyaltyRewardRedemptionStatus.PendingFulfillment ||
                redemptionStatus === LoyaltyRewardRedemptionStatus.Fulfilled) &&
              currentRedemptionHistoryEntries &&
              getIssuedByMarkupOfActingUserByCurrentRedemptionStatus(currentRedemptionHistoryEntries, redemptionStatus)}

            {requestedRedemptionHistoryEntry && redemptionStatus !== LoyaltyRewardRedemptionStatus.PendingFulfillment && (
              <Flex>
                <Box>
                  <Icon name="calendarInfo" size="md" />
                </Box>
                <Text ml={2} size="sm">
                  Claimed On: {format(new Date(requestedRedemptionHistoryEntry.createdAt), 'MM/dd/yy ')}
                </Text>
              </Flex>
            )}
          </Box>
          <Box mt="1px">
            <Text as="p" size="xs" lineHeight={6}>
              {source === LoyaltyPointDeltaSource.RewardRedemption && currentRedemption
                ? format(new Date(currentRedemption.updatedAt), 'MM/dd/yy')
                : format(new Date(node.createdAt), 'MM/dd/yy')}
            </Text>
          </Box>
        </Flex>
      </Box>
    </Flex>
  );
};
